import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// Configurações globais de SEO
const configureSEO = () => {
  // Configurar viewport meta tag se não estiver no HTML
  if (!document.querySelector('meta[name="viewport"]')) {
    const viewport = document.createElement('meta');
    viewport.name = 'viewport';
    viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=5.0';
    document.head.appendChild(viewport);
  }

  // Configurar charset se não estiver no HTML
  if (!document.querySelector('meta[charset]')) {
    const charset = document.createElement('meta');
    charset.setAttribute('charset', 'UTF-8');
    document.head.insertBefore(charset, document.head.firstChild);
  }

  // Adicionar preconnect para recursos externos
  const preconnects = [
    'https://fonts.googleapis.com',
    'https://fonts.gstatic.com',
    'https://cdnjs.cloudflare.com',
    'https://static.wixstatic.com'
  ];

  preconnects.forEach(url => {
    if (!document.querySelector(`link[href="${url}"]`)) {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = url;
      if (url.includes('gstatic')) {
        link.crossOrigin = 'anonymous';
      }
      document.head.appendChild(link);
    }
  });

  // Configurar meta theme-color para PWA
  if (!document.querySelector('meta[name="theme-color"]')) {
    const themeColor = document.createElement('meta');
    themeColor.name = 'theme-color';
    themeColor.content = '#2563eb'; // Cor azul da Dygito
    document.head.appendChild(themeColor);
  }

  // Adicionar meta para controle de cache
  const cacheControl = document.createElement('meta');
  cacheControl.httpEquiv = 'Cache-Control';
  cacheControl.content = 'public, max-age=31536000';
  document.head.appendChild(cacheControl);
};

// Função para otimizar Web Vitals e enviar dados para analytics
const sendToAnalytics = (metric) => {
  // Enviar métricas para Google Analytics 4
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', metric.name, {
      custom_parameter_1: metric.value,
      custom_parameter_2: metric.id,
      custom_parameter_3: metric.name,
    });
  }

  // Enviar para console em desenvolvimento
  if (process.env.NODE_ENV === 'development') {
    console.log('Web Vital:', metric);
  }

  // Aqui você pode adicionar outros serviços de analytics como:
  // - Hotjar
  // - Adobe Analytics  
  // - Mixpanel
  // - Seu próprio endpoint de analytics
};

// Função para lazy loading de recursos não críticos
const loadNonCriticalResources = () => {
  // Carregar fontes de forma otimizada
  const fontLink = document.createElement('link');
  fontLink.rel = 'stylesheet';
  fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap';
  fontLink.media = 'print';
  fontLink.onload = () => {
    fontLink.media = 'all';
  };
  document.head.appendChild(fontLink);

  // Preload de imagens críticas
  const criticalImages = [
    '/dygito.png',
    '/jornada.png',
    '/representacao-da-experiencia-utilizador-e-design-da-interface.jpg'
  ];

  criticalImages.forEach(src => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    document.head.appendChild(link);
  });
};

// Service Worker para cache e performance
const registerServiceWorker = () => {
  if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('SW registered: ', registration);
        })
        .catch((registrationError) => {
          console.log('SW registration failed: ', registrationError);
        });
    });
  }
};

// Configurar Google Tag Manager (substitua GTM-XXXXXX pelo seu ID)
const setupGTM = () => {
  if (process.env.NODE_ENV === 'production') {
    // Google Tag Manager
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','GTM-TQ9VR69V'); // Substitua pelo seu ID
  }
};

// Configurar Schema.org adicional no nível da aplicação
const setupGlobalSchema = () => {
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Dygito",
    "alternateName": "Dygito - Engenharia de Dados e BI",
    "url": "https://www.dygito.com.br",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://www.dygito.com.br/search?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    },
    "sameAs": [
      "https://www.linkedin.com/company/dygito",
      "https://wa.me/554333227627"
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.dygito.com.br"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Serviços",
        "item": "https://www.dygito.com.br#services"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Contato",
        "item": "https://www.dygito.com.br#contact"
      }
    ]
  };

  // Adicionar schemas ao head
  [websiteSchema, breadcrumbSchema].forEach((schema, index) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    script.id = `global-schema-${index}`;
    document.head.appendChild(script);
  });
};

// Função principal de inicialização
const initializeApp = () => {
  // Executar configurações de SEO
  configureSEO();
  
  // Configurar analytics
  setupGTM();
  
  // Configurar schemas globais
  setupGlobalSchema();
  
  // Registrar service worker
  registerServiceWorker();
  
  // Carregar recursos não críticos após o carregamento inicial
  setTimeout(loadNonCriticalResources, 100);
  
  // Renderizar a aplicação
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
};

// Executar inicialização
initializeApp();

// Configurar reportWebVitals com analytics
reportWebVitals(sendToAnalytics);

// Adicionar listener para mudanças de visibilidade (importante para SEO)
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    // Página saiu de foco - útil para analytics
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'page_visibility', {
        'event_category': 'engagement',
        'event_label': 'hidden'
      });
    }
  }
});

// Adicionar listener para scroll (engagement)
let scrollTimeout;
window.addEventListener('scroll', () => {
  clearTimeout(scrollTimeout);
  scrollTimeout = setTimeout(() => {
    const scrollPercent = Math.round(
      (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
    );
    
    // Marcar marcos de scroll para analytics
    if (scrollPercent === 25 || scrollPercent === 50 || scrollPercent === 75 || scrollPercent === 100) {
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'scroll_depth', {
          'event_category': 'engagement',
          'event_label': `${scrollPercent}%`
        });
      }
    }
  }, 100);
});

// Error boundary global para SEO (capturar erros JS que podem afetar indexação)
window.addEventListener('error', (event) => {
  console.error('Global error:', event.error);
  
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'exception', {
      'description': event.error?.message || 'Unknown error',
      'fatal': false
    });
  }
});

// Adicionar evento para carregamento completo da página
window.addEventListener('load', () => {
  // Marcar que a página carregou completamente
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'page_loaded', {
      'event_category': 'performance',
      'event_label': 'complete'
    });
  }

  // Configurar intersection observer para lazy loading de imagens
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.classList.remove('lazy');
            observer.unobserve(img);
          }
        }
      });
    });

    // Observar todas as imagens lazy
    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  }
});